import { AsyncLabelProvider, useAsyncLabelStore, type LabelLookup } from "@akin2unde/flowui-react";
import { useEffect, type ReactNode } from "react";
import { apiClient } from "../api/ApiClient";
import { useAuth } from "../auth/useAuth";
import { useWarehouse } from "../warehouse/WarehouseContext";
import type { KeyValue } from "../models/KeyValue";

const lookups: Record<string, LabelLookup> = Object.fromEntries(
  ["customer", "supplier", "product", "warehouse", "unit", "category", "staff"].map(entity => [entity, {
    async resolve(codes, {signal}) {
      const response = await apiClient.post<KeyValue[]>("lookup/ResolveLabels", {
        Entity: entity, Codes: [...codes], Field: "Name",
      }, {signal, showBusyIndicator: false, showError: false});
      return Object.fromEntries(response.data.map(row => [row.Key, String(row.Value ?? row.Key)]));
    },
  } satisfies LabelLookup]),
);
function InvalidateLabels() {
  const store = useAsyncLabelStore();
  useEffect(() => {
    const invalidate = () => store.invalidate();
    window.addEventListener("pantrystack:data-changed", invalidate);
    return () => window.removeEventListener("pantrystack:data-changed", invalidate);
  }, [store]);
  return null;
}
export default function LookupProvider({children}: {children: ReactNode}) {
  const {state} = useAuth();
  const {Warehouse} = useWarehouse();
  // A provider-owned cache; changing account/token/warehouse cancels obsolete work.
  const scopeKey = `${state.User?.Tenant ?? ""}:${state.User?.Code ?? ""}:${Warehouse?.Code ?? ""}:${state.Token ?? "signed-out"}`;
  return <AsyncLabelProvider lookups={lookups} scopeKey={scopeKey} options={{batchWindowMs: 20, maxBatchSize: 200, maxConcurrentBatches: 2, maxCacheEntries: 1500, cacheTtlMs: 300000, missingTtlMs: 30000, requestTimeoutMs: 15000}}>
    <InvalidateLabels />{children}
  </AsyncLabelProvider>;
}
